import { ScheduleGridBodyCellProps } from '@intrasync/react';

interface AccountingTransaction {
  id: string | number;
  type: "Invoice" | "Bill";
  number: string;
  customerSupplier: string;
  amount: number;
  dueDate: string;
  status: string;
}

interface AccountingScheduleRightBodyCellProps
  extends ScheduleGridBodyCellProps {
  itemsFromEntity: AccountingTransaction[];
  currentHeader?: {
    key: string;
    title: string;
    headerKey?: string;
  };
}

const AccountingScheduleRightBodyCell = (
  props: AccountingScheduleRightBodyCellProps
) => {
  if (props.itemsFromEntity?.length === 0) {
    return (
      <div
        style={{
          border: "1px solid #e0e0e0",
          ...props.style,
          backgroundColor: "#f8f9fa",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9e9e9e",
          fontSize: "11px",
          fontStyle: "italic",
        }}
      >
        No items
      </div>
    );
  }

  const { itemsFromEntity, currentHeader } = props;

  // Filter items to only show those that match the current column type
  // The ScheduleGrid should already filter these, but we'll add extra safety
  const filteredItems = currentHeader?.key
    ? itemsFromEntity.filter((item) => item.type === currentHeader.key)
    : itemsFromEntity;

  // If no items match this column, show empty cell
  if (filteredItems.length === 0) {
    return (
      <div
        style={{
          border: "1px solid #e0e0e0",
          ...props.style,
          backgroundColor: "#f8f9fa",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9e9e9e",
          fontSize: "11px",
          fontStyle: "italic",
        }}
      >
        No {currentHeader?.title?.toLowerCase() || "items"}
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "overdue":
        return "#f44336";
      case "due-soon":
        return "#ff9800";
      case "outstanding":
        return "#2196f3";
      default:
        return "#4caf50";
    }
  };

  const getTypeColor = (type: string) => {
    return type === "Invoice" ? "#2e7d32" : "#d32f2f";
  };

  const getTypeBackgroundColor = (type: string) => {
    return type === "Invoice" ? "#e8f5e8" : "#ffebee";
  };

  return (
    <div
      style={{
        border: "1px solid #e0e0e0",
        ...props.style,
        padding: filteredItems.length === 1 ? "6px" : "0",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        backgroundColor: "#fafafa",
      }}
    >
      {filteredItems.map((item: AccountingTransaction, index: number) => (
        <div
          key={`${item.id}-${index}`}
          style={{
            border: `2px solid ${getTypeColor(item.type)}`,
            borderRadius: "8px",

            marginRight: filteredItems.length === 1 ? "12px" : "0",
            padding: "8px",
            fontSize: "12px",
            backgroundColor: getTypeBackgroundColor(item.type),
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            transition: "all 0.2s ease",
          }}
          title={`${item.type} ${item.number} - ${item.customerSupplier
            } - ${formatCurrency(item.amount)}`}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
          }}
          onClick={() => {
            // TODO: Navigate to item details
          }}
        >
          <div
            style={{
              fontWeight: "600",
              color: getTypeColor(item.type),
              fontSize: "11px",
              marginBottom: "3px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: getTypeColor(item.type),
                display: "inline-block",
              }}
            />
            {item.type} {item.number}
          </div>
          <div
            style={{
              fontSize: "10px",
              color: "#546e7a",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginBottom: "3px",
              fontWeight: "500",
            }}
          >
            {item.customerSupplier}
          </div>
          <div
            style={{
              fontWeight: "700",
              fontSize: "11px",
              color: getStatusColor(item.status),
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{formatCurrency(item.amount)}</span>
            <span
              style={{
                fontSize: "9px",
                backgroundColor: getStatusColor(item.status),
                color: "white",
                padding: "1px 4px",
                borderRadius: "4px",
                textTransform: "uppercase",
                fontWeight: "600",
              }}
            >
              {item.status.replace("-", " ")}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AccountingScheduleRightBodyCell;
